import { Condition, Conditional, isRule, Operator, Rule } from '@dadohr/shared/domainTypes/rules';
import styled from '@emotion/styled';
import {
  Button
} from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { GroupIcon, Plus, PlusCircle } from 'lucide-react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FlexContainer } from '../../../../layout/Flex';
import { Cond } from './Condition';
import { EMPTY_RULE_GROUP } from './constants';
import { Props, RuleBuilderConfig } from './types';
import {
  createEmptyRuleGroup,
  getCanRemove,
  getEmptyCondition,
  getEmptyDefaultCondition,
  handleConditionChange,
  handleExpandedRuleChange,
  handleNestedRuleChange,
  isEmptyRule,
  isEmptyRuleGroup,
  isExpandedDateRule,
  isExpandedStringRule
} from './utils';

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  '& .MuiToggleButtonGroup-root': {
    borderRadius: '20px',
  },
  '& .MuiToggleButton-root': {
    borderRadius: '20px !important',
    border: `1px solid ${theme.palette.primary.main}`,
    color: theme.palette.primary.main,
    padding: '4px 16px',
    fontSize: '14px',
    fontWeight: 500,
    textTransform: 'none',
    minWidth: '48px',
    height: '32px',
    '&.Mui-selected': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
    },
    '&:not(.Mui-selected):hover': {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.primary.contrastText,
    },
    '&:first-of-type': {
      borderTopRightRadius: '4px !important',
      borderBottomRightRadius: '4px !important',
      marginRight: '-1px',
    },
    '&:last-of-type': {
      borderTopLeftRadius: '4px !important',
      borderBottomLeftRadius: '4px !important',
    },
  },
}));

const ConditionalText = styled('div')<{root?: boolean}>(({ theme, root }) => ({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  padding: `${ root ? '0' : '0 16px'}`,
  fontWeight: 'bold',
  fontSize: '14px',
  color: theme.palette.primary.main,
}));

const ElementsContainer = styled('div')`
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.theme.spacing()}px;
`;

const Actions = styled('div')`
  display: flex;
  gap: ${(p) => p.theme.spacing(1.5)}px;
`;

const FilterGroupButton = styled(Button)<{compact: boolean}>`
  padding-left: ${(p) => p.compact ? p.theme.spacing(1) : 0 }px !important;
  padding-bottom: ${(p) => p.compact ? p.theme.spacing(1) : 0}px !important;
`;

const GroupContainer = styled('div')<{ root: boolean, compact: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.theme.spacing()}px;
  padding: 0;
  /* Normal variant: the root's rows span the full width and nested groups
     end at the same edge; compact keeps its inset root and overhanging
     nested groups. */
  padding-right: ${(p) =>
    p.compact ? p.theme.spacing(1) : p.root ? 0 : p.theme.spacing(2)}px;
  border-radius: ${(p) => p.theme.custom.border.radius()}px;
  background: ${(p) => p.root ? 'transparent' : p.theme.custom.colors.gray.w100}; /* Remove background for root */
  ${(p) => (p.root || !p.compact ? '' : `margin-right: -${p.theme.spacing(2)}px;`)}
  width: ${(p) =>
    p.root
      ? '100%'
      : p.compact
      ? `calc(100% - ${p.theme.spacing(1)}px)`
      : 'calc(100% - 24px)'};
  position: relative;

  /* Add vertical line with circles for non-root groups */
  ${(p) => !p.root ? `
    margin-left: 24px; /* Space for the line and circles */
    
    /* Vertical line */
    &::before {
      content: '';
      position: absolute;
      left: -16px;
      top: 8px;
      bottom: 8px;
      width: 1px;
      background-color: ${p.theme.palette.grey[300]};
    }
    
    /* Top circle */
    &::after {
      content: '';
      position: absolute;
      left: -20px;
      top: 4px;
      width: 9px;
      height: 9px;
      border-radius: 50%;
      background-color: ${p.theme.palette.grey[300]};
    }

    .MuiToggleButtonGroup-root {
      margin-left: 12px;
    }
  ` : ''}

  /* Bottom circle - only for non-root groups */
  ${(p) => !p.root ? `
    & > :last-child::after {
      content: '';
      position: absolute;
      left: -20px;
      bottom: 4px;
      width: 9px;
      height: 9px;
      border-radius: 50%;
      background-color: ${p.theme.palette.grey[300]};
    }
  ` : ''}
`;

const ConditionalToggle = ({
  value,
  onChange,
  root
}: {
  value: Conditional;
  onChange: (nextValue: Conditional) => void;
  root: boolean
}) => {
  return (
    <StyledToggleButtonGroup
      value={value}
      exclusive
      onChange={(_: any, newValue: any) => {
        if (newValue) onChange(newValue);
      }}
      size="small"
    >
      <ToggleButton value="all">AND</ToggleButton>
      <ToggleButton value="any">OR</ToggleButton>
    </StyledToggleButtonGroup>
  );
};

export const AddButton = ({
  label,
  onClick
}: {
  label: React.ReactNode;
  onClick: () => void;
}) => {
  return (
    <Button
      color="primary"
      size="small"
      startIcon={<PlusCircle size={18} />}
      onClick={() => onClick()}
    >
      {label}
    </Button>
  );
};

const Group = <T extends string>({
  value,
  onChange,
  config,
  root,
  variant,
  showEmptyRowWhenNull = true,
  newUx = false,
  extraRootActions
}: {
  value: Rule<T> | null;
  onChange: (nextvalue: Rule<T> | null) => void;
  config: RuleBuilderConfig<T>;
  root?: boolean;
  variant: 'compact' | 'normal';
  showEmptyRowWhenNull?: boolean;
  // NEW_UX: render the "Add filter" actions in the editor's blue600 accent.
  newUx?: boolean;
  // Opt-in extra action(s) rendered alongside "Add filter" / "Add filter
  // group" on the root row only — lets a specific consumer (e.g. the Task
  // Builder's audience editor) add its own button there without leaking
  // that consumer's concerns into this shared component.
  extraRootActions?: React.ReactNode;
}) => {
  const { t } = useTranslation();
  if (value === null) {
    return null;
  }

  const getNewCondition = (): Condition<T> => {
    if (showEmptyRowWhenNull) {
      return getEmptyCondition() as Condition<T>
    } else {
      return getEmptyDefaultCondition(config);
    }
  }

  // Check if this rule represents an expanded string or date condition
  const expandedCheck = isExpandedStringRule(value, config);
  const expandedDateCheck = isExpandedDateRule(value, config);

  if (
    expandedDateCheck.isExpanded &&
    expandedDateCheck.key &&
    expandedDateCheck.value !== undefined &&
    expandedDateCheck.operator
  ) {
    // Render as a single "is on"/"is not on" condition row instead of a group
    const condition: Condition<T> = {
      k: expandedDateCheck.key,
      op: expandedDateCheck.operator as Operator,
      v: expandedDateCheck.value
    };

    return (
      <Cond
        value={condition}
        onChange={(nextCond) => {
          const result = handleExpandedRuleChange(
            nextCond,
            config,
            expandedDateCheck.operator
          );
          onChange(result);
        }}
        config={config}
        variant={variant}
        canRemove={true}
        root={root}
      />
    );
  }

  // Early check for single condition with empty array - bypass expanded rule logic entirely
  if (value.els.length === 1 && !isRule(value.els[0])) {
    const singleCondition = value.els[0] as Condition<T>;
    if (Array.isArray(singleCondition.v) && singleCondition.v.length === 0 && 
        ['isAnyOf', 'isNoneOf', 'containsAnyOf', 'doesNotContainAnyOf'].includes(singleCondition.op)) {
      // Skip all expanded rule logic and render normally as a group
    } else if (expandedCheck.isExpanded && expandedCheck.key && expandedCheck.operator && expandedCheck.values) {
      // Render as a single condition row instead of a group
      const condition: Condition<T> = {
        k: expandedCheck.key,
        op: expandedCheck.operator as Operator,
        v: expandedCheck.values
      };
      
      return (
        <Cond
          value={condition}
          onChange={(nextCond) => {
            const result = handleExpandedRuleChange(nextCond, config, expandedCheck.operator);
            onChange(result);
          }}
          config={config}
          variant={variant}
          canRemove={true}
          root={root}
        />
      );
    }
  } else if (expandedCheck.isExpanded && expandedCheck.key && expandedCheck.operator && expandedCheck.values) {
    // Render as a single condition row instead of a group
    const condition: Condition<T> = {
      k: expandedCheck.key,
      op: expandedCheck.operator as Operator,
      v: expandedCheck.values
    };
    
    return (
      <Cond
        value={condition}
        onChange={(nextCond) => {
          if (nextCond === null) {
            onChange(null);
          } else {
            const result = handleExpandedRuleChange(nextCond, config, expandedCheck.operator);
            onChange(result);
          }
        }}
        config={config}
        variant={variant}
        canRemove={true}
        root={root}
      />
    );
  }

  // Check if we have any filled conditions
  const hasFilledConditions = value.els.some(el => {
    if (isRule(el)) {
      return !isEmptyRuleGroup(el);
    }
    return el.k !== '';
  });

  const totalElements = value.els.length;

  return (
    <GroupContainer root={!!root} compact={variant === 'compact'}>
      <ElementsContainer>
        {value.els.map((el, i) => {
          const hasNextElement = i < value.els.length - 1;
          
          // First separator (between elements 0 and 1) is always a toggle if there are 2+ elements
          const isFirstSeparator = i === 0 && totalElements >= 2;
          // All other separators are text if there are 3+ elements
          const isTextSeparator = i > 0 && hasNextElement && totalElements >= 3;

          return (
            <React.Fragment key={i}>
              {!root && i === 0 && (
                <FlexContainer paddingTop={1} style={{paddingLeft: '12px'}}>
                  <GroupIcon size={20} strokeWidth={1.5} />
                  <span style={{ fontSize: 13 }}>{t('Filter group')}</span>
                </FlexContainer>
              )}
              {isRule(el) ? (
                <Group
                  value={el}
                  onChange={(nextEl) => {
                    const result = handleNestedRuleChange(value, i, nextEl, !!root, config);
                    onChange(result);
                  }}
                  config={config}
                  variant={variant}
                  showEmptyRowWhenNull={showEmptyRowWhenNull}
                  newUx={newUx}
                />
              ) : (
                <Cond
                  value={el}
                  onChange={(nextEl) => {
                    const result = handleConditionChange(value, i, nextEl, !!root, showEmptyRowWhenNull, config);
                    onChange(result);
                  }}
                  config={config}
                  variant={variant}
                  canRemove={getCanRemove(root ? root : false, value.els.length, el.k, showEmptyRowWhenNull)}
                  root={root}
                />
              )}

              {/* Show separator between elements */}
              {isFirstSeparator && (
                <div style={{ display: 'flex', justifyContent: 'flex-start', padding: '0' }}>
                  <ConditionalToggle
                    value={value.type}
                    onChange={(type) => onChange({ ...value, type })}
                    root
                  />
                </div>
              )}
              
              {isTextSeparator && (
                <ConditionalText root={root}>
                  {value.type === 'all' ? 'AND' : 'OR'}
                </ConditionalText>
              )}
            </React.Fragment>
          );
        })}
        
        {hasFilledConditions && (
          <Actions>
            {!root ? (
              <FilterGroupButton
                compact={variant === 'compact'}
                color="primary"
                size="small"
                startIcon={<PlusCircle size={18} />}
                style={newUx ? { color: '#0061C4' } : undefined}
                onClick={() => {
                  onChange({
                    ...value,
                    els: [...value.els, getNewCondition()]
                  });
                }}
              >
                {t('Add filter to group')}
              </FilterGroupButton>
            ) : (
              <>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={<Plus size={18} />}
                  style={
                    newUx ? { color: '#0061C4', borderColor: '#0061C4' } : undefined
                  }
                  onClick={() => {
                    onChange({
                      ...value,
                      els: [...value.els, getNewCondition()]
                    });
                  }}
                >
                  {t('Add filter')}
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={<GroupIcon size={18} />}
                  style={
                    newUx ? { color: '#0061C4', borderColor: '#0061C4' } : undefined
                  }
                  onClick={() => {
                    onChange({
                      ...value,
                      els: [
                        ...value.els,
                        {
                          type: value.type === 'all' ? 'any' : 'all',
                          els: [getNewCondition()]
                        }
                      ]
                    });
                  }}
                >
                  {t('Add filter group')}
                </Button>
                {extraRootActions}
              </>
            )}
          </Actions>
        )}
      </ElementsContainer>
    </GroupContainer>
  );
};

export const EMPTY_RULE_GROUP_WITH_ONE_DEFUALT_CONDITION = <T extends string>(
  config: RuleBuilderConfig<T>
) => {
  return { ...EMPTY_RULE_GROUP, els: [getEmptyDefaultCondition(config)] };
};

export const RuleBuilder = <T extends string>({
  value,
  onChange,
  config,
  nullable,
  variant = 'normal',
  showEmptyRowWhenNull = true,
  newUx = false,
  extraRootActions
}: Props<T> & {
  nullable?: boolean;
  showEmptyRowWhenNull?: boolean;
  newUx?: boolean;
  extraRootActions?: React.ReactNode;
}) => {
  const workingValue = useMemo(() => {
    if (value !== null && value.els.length > 0) {
      return value;
    }
    
    if (showEmptyRowWhenNull) {
      return createEmptyRuleGroup<T>();
    } else {
      return {...EMPTY_RULE_GROUP_WITH_ONE_DEFUALT_CONDITION(config)}
    }
  }, [value, showEmptyRowWhenNull, config]);

  const handleChange = (nextRule: Rule<T> | null) => {
    // console.group('🔄 RuleBuilder Change');
    // console.log('Previous rule:', value);
    // console.log('New rule:', nextRule);
    
    // if (nextRule) {
    //   // Log expanded string rules detection
    //   const expandedCheck = isExpandedStringRule(nextRule, config);
    //   if (expandedCheck.isExpanded) {
    //     console.log('🔍 Detected expanded string rule:', {
    //       key: expandedCheck.key,
    //       operator: expandedCheck.operator,
    //       values: expandedCheck.values
    //     });
    //   }
      
    //   console.log('📊 Rule structure:', JSON.stringify(nextRule, null, 2));
    // }
    // console.groupEnd();
    
    if (showEmptyRowWhenNull && nextRule && isEmptyRuleGroup(nextRule)) {
      return onChange(nullable ? null : nextRule);
    }
    
    onChange(
      nullable && nextRule && isEmptyRule(nextRule) ? null : nextRule
    );
  };

  return (
    <Group
      value={workingValue}
      config={config}
      onChange={handleChange}
      root
      variant={variant}
      showEmptyRowWhenNull={showEmptyRowWhenNull}
      newUx={newUx}
      extraRootActions={extraRootActions}
    />
  );
};